'use client';
import { AutosaveContextProvider } from '@susu/custom-made/providers/AutosaveContextProvider';
import { SectionContainer } from '../sectionContainer/sectionContainer';
import { ClientType, ConfiguratorOrigin } from '@susu/custom-made/utils/constants';
import EnvironmentContextProvider from '@susu/custom-made/providers/EnvironmentContextProvider';
import { GlobalContextProvider } from '@susu/custom-made/providers/GlobalContextProvider';
import { UserContextProvider } from '@susu/custom-made/providers/UserContextProvider';
import { NavigationContextProvider } from '@susu/custom-made/providers/NavigationContextProvider';
import { FabricContextProvider } from '@susu/custom-made/providers/FabricContextProvider';
import { OptionContextProvider } from '@susu/custom-made/providers/OptionContextProvider';
import { MissingSelectionValuesContextProvider } from '@susu/custom-made/providers/MissingSelectionValuesContextProvider';
import { SizePassportContextProvider } from '@susu/custom-made/providers/SizePassportContextProvider';
import { ConfiguratorPayload } from '../configurator/types/Configurator.types';
import { getSortedDefaultLayers } from '../configurator/utils/helpers';
import { CustomMadeFeatureFlagsProvider } from '@susu/custom-made/feature-flags/FeatureFlagsProvider';
import Skeleton from '../skeleton/skeleton';
import { ClientI18NProvider } from '@susu/i18n/client';

export default function ConfiguratorClientContainer(props: Readonly<ConfiguratorPayload>) {
  const defaultLayers = getSortedDefaultLayers(props.defaultLayersObject, props.defaultProduct!);
  // The API sends an absent query parameter as `null`; the providers below
  // declare these as optional strings, and the section container reads an
  // empty `fabricPreFilters` as "no pre-filters".
  const fabricPreFilters = props.fabricPreFilters ?? '';
  return (
    <>
      <EnvironmentContextProvider
        env={props.env}
        datadogClientToken={props.datadogClientToken}
        datadogApplicationId={props.datadogApplicationId}
        scanditKey={props.scanditKey}
        configuratordataproviderBasePath={props.configuratordataproviderBasePath}
        productconfigurationBasePath={props.productconfigurationBasePath}
        productmeasurementBasePath={props.productmeasurementBasePath}
        addToCartWebstoreUrlTemplate={props.addToCartWebstoreUrlTemplate}
        configurationImagesBasePath={props.configurationImagesBasePath}
        createconfiguredproductimagePath={props.createconfiguredproductimagePath}
        cacheBasePath={props.cacheBasePath}
        bookingToolServiceId={props.bookingToolServiceId}
        cdnDomain={props.cdnDomain}
        cmWebstoreOrigin={props.cmWebstoreOrigin}
        datadogAppVersion={props.datadogAppVersion}
        datadogSessionSampleRate={props.datadogSessionSampleRate}
        datadogSessionReplaySampleRate={props.datadogSessionReplaySampleRate}
        datadogLogsSampleRate={props.datadogLogsSampleRate}
      >
      <GlobalContextProvider
        defaultDeliveryPeriod={props.defaultDeliveryPeriod}
        defaultCmCorrelationId={props.defaultCmCorrelationId}
      >
        <ClientI18NProvider 
          language={props.defaultLanguage} 
          messages={props.translations}
        >
          <UserContextProvider
            defaultAccountNr={props.defaultAccountNr ?? undefined}
            defaultCountryCode={props.defaultCountryCode}
            defaultClient={props.defaultClient as ClientType}
            defaultLanguage={props.defaultLanguage}
            defaultProduct={props.defaultProduct}
            defaultOrigin={props.origin}
            defaultRtwProductCodes={props.defaultRtwProductCodes}
            defaultStoreID={props.defaultStoreID ?? undefined}
            defaultLocationCode={props.defaultLocationCode ?? undefined}
            defaultSizeId={props.defaultSizeId ?? undefined}
            defaultChannelId={props.defaultChannelId ?? undefined}
          >
            <NavigationContextProvider defaultSection={props.defaultSection}>
              <FabricContextProvider defaultSelectedFabric={props.defaultSelectedFabric}>
                <OptionContextProvider
                  defaultSelectedOptions={props.defaultSelectedOptions}
                  defaultHiddenOptionsAndValues={props.defaultHiddenOptionsAndValues}
                  defaultSelectedMonogram={props.defaultSelectedMonogram}
                  defaultSelectedBaseStyle={props.defaultSelectedBaseStyle}
                  defaultIsAdditionalProductAdded={props.defaultIsAdditionalProductAdded}
                >
                  <MissingSelectionValuesContextProvider
                    defaultShowMissingFabricModal={props.defaultShowMissingFabricModal}
                    defaultShowMissingOptionsPanel={props.defaultShowMissingOptionsPanel}
                    defaultMonogramsIdsWithMissingSelection={props.defaultMonogramsIdsWithMissingSelection}
                    defaultMissingOptionValuesPerConfig={props.defaultMissingOptionValuesPerConfig}
                    defaultMissingMonogramValuesPerConfig={props.defaultMissingMonogramValuesPerConfig}
                    defaultMissingOptionsPerConfig={props.defaultMissingOptionsPerConfig}
                  >
                    <SizePassportContextProvider
                      defaultToken={props.defaultToken}
                      defaultSizePassportSelection={props.defaultSizePassportSelection}
                    >
                      <CustomMadeFeatureFlagsProvider
                        config={{
                          countryCode: props.defaultCountryCode,
                          client: props.defaultClient as ClientType,
                          product: props.defaultProduct!,
                          reorder: props.reorder ?? '',
                          sessionId: props.sessionId ?? '',
                          storeID: props.defaultStoreID ?? '',
                          search: props.search ?? '',
                          origin: props.origin as ConfiguratorOrigin,
                          isTest: props.isTest ?? undefined,
                        }}
                        fallback={<Skeleton product={props.defaultProduct} cdnDomain={props.cdnDomain} />}
                      >
                        <AutosaveContextProvider
                          fabricList={props.fabricList}
                          fabricSequence={props.fabricSequence}
                          options={props.options}
                          optionValues={props.optionValues}
                          defaultValues={props.defaultValues}
                          stylePresets={props.stylePresets}
                          businessRules={props.businessRules}
                          defaultSection={props.defaultSection}
                          fabricPreFilters={fabricPreFilters}
                          isInPreloadFlow={props.isInPreloadFlow}
                          isLoginFlow={props.isLoginFlow === 'true'}
                        >
                          <SectionContainer
                            fabricList={props.fabricList}
                            fabricSequence={props.fabricSequence}
                            fabricFilters={props.fabricFilters}
                            fabricPreFilters={fabricPreFilters}
                            stylePresets={props.stylePresets}
                            businessRules={props.businessRules}
                            options={props.options}
                            optionValues={props.optionValues}
                            defaultValues={props.defaultValues}
                            layerDefinitions={props.layerDefinitions}
                            datadogClientToken={props.datadogClientToken}
                            datadogApplicationId={props.datadogApplicationId}
                            countryCode={props.defaultCountryCode}
                            client={props.defaultClient as ClientType}
                            product={props.defaultProduct!}
                            isCustomizeFlow={props.defaultRtwProductCodes.length > 0}
                            origin={props.origin}
                            defaultLayers={defaultLayers}
                            defaultSection={props.defaultSection}
                            isInPreloadFlow={props.isInPreloadFlow}
                            currentDate={props.currentDate}
                          />
                        </AutosaveContextProvider>
                      </CustomMadeFeatureFlagsProvider>
                    </SizePassportContextProvider>
                  </MissingSelectionValuesContextProvider>
                </OptionContextProvider>
              </FabricContextProvider>
            </NavigationContextProvider>
          </UserContextProvider>
        </ClientI18NProvider>
      </GlobalContextProvider>
      </EnvironmentContextProvider>
    </>
  );
}
